import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import { fetchBlogPosts, fetchBlogCategories } from '../../services/sanityService';
import BlogPostCard from './components/BlogPostCard';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadPosts();
  }, [selectedCategory, searchTerm]);

  const loadCategories = async () => {
    const data = await fetchBlogCategories();
    setCategories(data);
  };

  const loadPosts = async () => {
    setLoading(true);
    const data = await fetchBlogPosts({
      categorySlug: selectedCategory,
      searchTerm: searchTerm
    });
    setPosts(data);
    setLoading(false);
  };

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setSearchTerm('');
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://openbooq.com/blog"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Roofing Blog - Expert Tips & Industry Insights | OpenbooQ</title>
        <meta 
          name="description" 
          content="Explore expert roofing tips, industry news, product reviews, and cost guides. Stay informed with the latest insights from OpenbooQ's roofing professionals." 
        />
        <meta name="keywords" content="roofing blog, roofing tips, roof maintenance, shingle reviews, roofing costs, home improvement" />
        <meta property="og:title" content="Roofing Blog - Expert Tips & Industry Insights | OpenbooQ" />
        <meta property="og:description" content="Expert roofing advice, industry news, and comprehensive guides for homeowners." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 text-white pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Roofing Insights & Expert Advice
              </h1>
              <p className="text-xl sm:text-2xl text-blue-100 max-w-3xl mx-auto">
                Stay informed with the latest roofing tips, industry trends, and comprehensive guides
              </p>
            </div>
          </div>
        </section>

        {/* Blog Intro SEO Copy */}
        <section className="bg-white py-10 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                The OpenbooQ blog is your resource for expert roofing knowledge — from understanding the differences between architectural and impact-resistant shingles, to navigating insurance claims after storm damage, to knowing when it's time to repair versus replace your roof. Our articles are written by roofing professionals with real-world installation experience, so you get practical, actionable information you can actually use.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you're a first-time homeowner preparing for your first roof replacement, or an experienced homeowner comparing shingle brands and warranty options, our blog covers the topics that matter most to homeowners nationwide. Browse by category to find articles on shingle reviews, cost guides, maintenance tips, and the latest industry news.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="bg-white border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <SearchBar 
              searchTerm={searchTerm}
              onSearchChange={handleSearch}
            />
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Categories */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-36">
                <CategoryFilter
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
                
                {(selectedCategory || searchTerm) && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Clear Filters
                  </button>
                )}
              </div>
            </aside>

            {/* Blog Posts Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading posts...</p>
                  </div>
                </div>
              ) : posts?.length > 0 ? (
                <>
                  <div className="mb-6 flex items-center justify-between">
                    <p className="text-gray-600">
                      {posts?.length} {posts?.length === 1 ? 'post' : 'posts'} found
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {posts?.map((post) => (
                      <BlogPostCard key={post?._id} post={post} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <Icon name="FileText" size={64} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No posts found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {searchTerm || selectedCategory
                      ? 'Try adjusting your filters or search terms' :'No blog posts have been published yet. Check back soon!'}
                  </p>
                  {(searchTerm || selectedCategory) && (
                    <button
                      onClick={clearFilters}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Icon name="RotateCcw" size={16} />
                      Reset Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;